/**
 * Copyright (C) Boost commerce
 * This file is part of commercial Boost commerce projects
 *
 * This file can not be copied and/or distributed without the express
 * permission of Boost commerce
 *
 * Created on Thu Nov 23 2023 15:26:59
 */
// @flow

import _ from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { withI18n } from '@shopify/react-i18n';
import Tour from 'reactour';
import NoteIcon from 'src/assets/images/standard-search/notes_minor.svg';
import Navigation from 'src/components/views/standard-search/guide-tour/navigation';
import Step from 'src/components/views/standard-search/guide-tour/step';
import GuideTourStep2 from 'src/assets/images/standard-search/guide-tour-step-2.gif';
import GuideTourStep3 from 'src/assets/images/standard-search/guide-tour-step-3.gif';
import GuideTourStep4 from 'src/assets/images/standard-search/guide-tour-step-4.gif';
import GuideTourStep5 from 'src/assets/images/standard-search/guide-tour-step-5.gif';
import GuideTourStep6 from 'src/assets/images/standard-search/guide-tour-step-6.gif';
import GuideTourStep7 from 'src/assets/images/standard-search/guide-tour-step-7.gif';
import GuideTourStep8 from 'src/assets/images/standard-search/guide-tour-step-8.gif';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

const STEP_IMAGES = [
  null,
  GuideTourStep2,
  GuideTourStep3,
  GuideTourStep4,
  GuideTourStep5,
  GuideTourStep6,
  GuideTourStep7,
  GuideTourStep8
];

const STEP_COUNT = _.size(STEP_IMAGES);

export const GuideTour = props => {
  const { i18n } = props;

  const [tourOpened, setTourOpened] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Toggle open/close guide tour
  const toggleTour = useCallback(() => {
    setTourOpened(!tourOpened);
  }, [tourOpened]);

  // Listen event emitter for trigger open guide tour from welcome popup
  useEffect(() => {
    Emitter.on(EVENT_KEY.START_STANDARD_SEARCH_GUIDE_TOUR, toggleTour);

    return () => {
      Emitter.off(EVENT_KEY.START_STANDARD_SEARCH_GUIDE_TOUR, toggleTour);
    };
  }, [toggleTour]);

  // Disable entire page scroll when guide tour is opened to avoid render wrong position
  const enableScroll = useCallback(() => {
    document.documentElement.style.overflowY = 'auto';
  }, []);

  // Re-enable page scroll after closing guide tour
  const disableScroll = useCallback(() => {
    document.documentElement.style.overflowY = 'hidden';
  }, []);

  // Build steps for guide tour
  const steps = useMemo(() => {
    return _(STEP_IMAGES)
      .map((image, index) => {
        const stepNumber = index + 1;
        return {
          selector: `[data-tour="search-guide-tour-step-${stepNumber}"]`,
          content: props => {
            return (
              <Step
                image={image && <img src={image} alt={`guide-tour-step-${stepNumber}`} />}
                heading={i18n.translate(
                  `AdminHTML.StandardSearch.GuideTour.Step${stepNumber}.title`
                )}
                description={i18n.translate(
                  `AdminHTML.StandardSearch.GuideTour.Step${stepNumber}.content`
                )}
                pagination={
                  <Navigation
                    stepNumber={stepNumber}
                    totalSteps={STEP_COUNT}
                    nextStep={() => {
                      if (stepNumber < STEP_COUNT) {
                        // Next
                        props.goTo(stepNumber);
                      } else {
                        // Finish
                        toggleTour();
                      }
                    }}
                  />
                }
              />
            );
          },
          position: 'right',
          style: {
            marginLeft: '14px',
            borderRadius: '8px',
            backgroundColor: 'white',
            padding: 0,
            width: '380px',
            maxWidth: 'unset',
            color: 'unset'
          },
          resizeObservables: [`[data-tour="observable-parent"]`]
        };
      })
      .value();
  }, [i18n, toggleTour]);

  return (
    <Fragment>
      <div className="Trigger" onClick={toggleTour}>
        <img src={NoteIcon} alt="ok-icon" onClick={() => this.rate('ok')} />
        <p>{i18n.translate('AdminHTML.StandardSearch.GuideTour.title')}</p>
      </div>
      <Tour
        startAt={0}
        rounded={8}
        maskSpace={0}
        scrollDuration={200}
        steps={steps}
        isOpen={tourOpened}
        goToStep={currentStep}
        onRequestClose={toggleTour}
        getCurrentStep={setCurrentStep}
        onAfterOpen={disableScroll}
        onBeforeClose={enableScroll}
        showNumber={false}
        showButtons={false}
        showNavigation={false}
        closeWithMask={false}
      />
    </Fragment>
  );
};

export default withI18n()(GuideTour);
